import { useEffect, useRef, useState } from "react";
import { RefreshCw } from "lucide-react";

/**
 * PullToRefresh — wraps a page. When the user pulls down past a threshold from the top,
 * triggers `onRefresh`. Native-feeling on iOS/Android, no-op on desktop.
 *
 * Usage:
 *   <PullToRefresh onRefresh={async () => await reloadData()}>
 *     ... page content ...
 *   </PullToRefresh>
 */
export default function PullToRefresh({ onRefresh, threshold = 70, children }) {
  const [pull, setPull] = useState(0); // current pull distance in px
  const [refreshing, setRefreshing] = useState(false);
  const startY = useRef(null);
  const startX = useRef(null);
  const armed = useRef(false);

  useEffect(() => {
    // Only attach on touch-capable devices. Desktop = no-op.
    if (typeof window === "undefined" || !("ontouchstart" in window)) return;

    const onTouchStart = (e) => {
      try {
        if (window.scrollY <= 0) {
          startY.current = e.touches[0].clientY;
          startX.current = e.touches[0].clientX;
          armed.current = true;
        } else {
          armed.current = false;
          startY.current = null;
          startX.current = null;
        }
      } catch (err) { /* ignore */ }
    };
    const onTouchMove = (e) => {
      try {
        if (!armed.current || startY.current == null || refreshing) return;
        const dy = e.touches[0].clientY - startY.current;
        const dx = e.touches[0].clientX - startX.current;
        // If gesture is horizontal-dominant OR upward, disarm — user is scrolling sideways or up.
        if (Math.abs(dx) > Math.abs(dy) || dy < 0) {
          armed.current = false;
          if (pull !== 0) setPull(0);
          return;
        }
        if (dy > 0) {
          const damped = Math.min(threshold * 1.6, dy * 0.5);
          setPull(damped);
        }
      } catch (err) { /* ignore */ }
    };
    const onTouchEnd = async () => {
      try {
        if (refreshing) return;
        if (pull >= threshold && armed.current) {
          setRefreshing(true);
          setPull(threshold);
          try { await onRefresh(); } catch (e) { /* swallow */ }
          setRefreshing(false);
        }
        setPull(0);
        startY.current = null;
        startX.current = null;
        armed.current = false;
      } catch (err) { /* ignore */ }
    };

    document.addEventListener("touchstart", onTouchStart, { passive: true });
    document.addEventListener("touchmove", onTouchMove, { passive: true });
    document.addEventListener("touchend", onTouchEnd, { passive: true });
    document.addEventListener("touchcancel", onTouchEnd, { passive: true });
    return () => {
      document.removeEventListener("touchstart", onTouchStart);
      document.removeEventListener("touchmove", onTouchMove);
      document.removeEventListener("touchend", onTouchEnd);
      document.removeEventListener("touchcancel", onTouchEnd);
    };
  }, [pull, refreshing, threshold, onRefresh]);

  const showIndicator = pull > 0 || refreshing;
  const ratio = Math.min(1, pull / threshold);

  return (
    <div style={{ position: "relative" }}>
      {showIndicator && (
        <div
          aria-hidden="true"
          style={{
            position: "fixed",
            top: `calc(env(safe-area-inset-top, 0px) + 12px)`,
            left: "50%",
            transform: `translateX(-50%) translateY(${refreshing ? 0 : Math.max(0, pull - 30)}px)`,
            zIndex: 60,
            background: "var(--surface2)",
            border: "1px solid var(--border)",
            borderRadius: "999px",
            width: 36,
            height: 36,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            opacity: Math.max(0.4, ratio),
            transition: refreshing ? "transform 0.2s" : "none",
            boxShadow: "0 4px 16px rgba(0,0,0,0.4)",
          }}
        >
          <RefreshCw
            size={16}
            style={{
              color: "var(--lime)",
              transform: refreshing ? "rotate(0deg)" : `rotate(${ratio * 360}deg)`,
              animation: refreshing ? "spin 1s linear infinite" : "none",
            }}
          />
        </div>
      )}
      <div
        style={{
          transform: pull > 0 && !refreshing ? `translateY(${pull * 0.4}px)` : "none",
          transition: refreshing ? "transform 0.25s ease-out" : pull === 0 ? "transform 0.25s ease-out" : "none",
        }}
      >
        {children}
      </div>
    </div>
  );
}
